import { router } from "@inertiajs/core";
import { captureMessage } from "~/observability";
import { confirmLocationSwitch } from "~/common/location_switch_confirm";
import { getLocationName } from "~/util/location_registry";
import { isCrossLocationNavigation } from "~/util/location_url";

let pending = false;
let activeCleanup: (() => void) | null = null;
let intentionalLocationSwitchUrl: string | null = null;
let intentionalLocationSwitchCleanup: ReturnType<typeof setTimeout> | null = null;
const INTENTIONAL_LOCATION_SWITCH_ATTRIBUTE = "data-intentional-location-switch";

// All three entry points cancel the navigation first, then share this: ask, then either stay or
// leave with a full page load — the layout's permanent islands read their location once at mount.
function confirmThenNavigate(cross: { from: string; to: string }, destinationUrl: string) {
  pending = true;
  const { promise, cleanup } = confirmLocationSwitch();
  activeCleanup = cleanup;

  promise
    .then((confirmed) => {
      activeCleanup = null;
      pending = false;
      logLocationSwitchGuard(cross, confirmed ? "continued" : "stayed", destinationUrl);
      if (confirmed) window.location.href = destinationUrl;
    })
    .catch(() => {
      cleanupDialog();
    });
}

function clearIntentionalLocationSwitch() {
  intentionalLocationSwitchUrl = null;
  if (intentionalLocationSwitchCleanup) {
    clearTimeout(intentionalLocationSwitchCleanup);
    intentionalLocationSwitchCleanup = null;
  }
}

function bypassNextLocationNavigation(destinationUrl: string) {
  clearIntentionalLocationSwitch();
  intentionalLocationSwitchUrl = destinationUrl;
  intentionalLocationSwitchCleanup = setTimeout(clearIntentionalLocationSwitch, 5000);
}

function consumeIntentionalLocationSwitch(destinationUrl: string) {
  if (intentionalLocationSwitchUrl !== destinationUrl) return false;

  clearIntentionalLocationSwitch();
  return true;
}

function logLocationSwitchGuard(
  cross: { from: string; to: string },
  action: "continued" | "stayed",
  destinationUrl: string,
) {
  const data = {
    fromLocationId: cross.from,
    fromLocationName: getLocationName(cross.from),
    toLocationId: cross.to,
    toLocationName: getLocationName(cross.to),
    action,
    destinationUrl,
    currentUrl: window.location.href,
  };

  captureMessage("location_switch_guard_triggered", {
    level: "info",
    fingerprint: ["location_switch_guard"],
    extra: data,
  });
}

function cleanupDialog() {
  pending = false;
  if (activeCleanup) {
    activeCleanup();
    activeCleanup = null;
  }
}

type BeforeFetchEventDetail = {
  url: URL;
  fetchOptions: { method?: string; headers: Record<string, string> };
};

/**
 * Installs guards that show a confirmation dialog before switching location
 * portals. Covers link clicks (capture phase), Turbo fetches, and Inertia visits.
 */
export function installCrossLocationTurboGuard() {
  // Capture-phase click listener catches <a> clicks before stopPropagation can
  // prevent them from reaching Turbo/Inertia handlers. stopImmediatePropagation
  // ensures the Turbo click handler does not also fire for the same click, which
  // would otherwise trigger a second turbo:before-fetch-request guard.
  document.addEventListener(
    "click",
    (event) => {
      if (pending) return;

      const link = (event.target as Element).closest<HTMLAnchorElement>("a[href]");
      if (!link) return;

      // skip modifier-key clicks (new tab / new window) and non-left-clicks
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0)
        return;

      // skip links that open in a new tab
      if (link.target === "_blank") return;

      const cross = isCrossLocationNavigation(link.href);
      if (!cross) return;

      if (link.hasAttribute(INTENTIONAL_LOCATION_SWITCH_ATTRIBUTE)) {
        bypassNextLocationNavigation(link.href);
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      confirmThenNavigate(cross, link.href);
    },
    true, // capture phase
  );

  // Turbo fetch guard for form submissions and turbo-initiated navigations
  document.addEventListener("turbo:before-fetch-request", ((
    event: CustomEvent<BeforeFetchEventDetail>,
  ) => {
    const { url, fetchOptions } = event.detail;

    if (pending) return;

    // skip turbo frame loads — they don't change the URL bar
    const turboFrameHeader = fetchOptions.headers["Turbo-Frame"];
    if (turboFrameHeader && turboFrameHeader !== "_top") return;

    // skip hover prefetches — the user hasn't actually clicked anything
    if (fetchOptions.headers["X-Sec-Purpose"] === "prefetch") return;

    // skip non-GET requests — those are actions, not navigations. If the
    // response triggers a cross-location redirect, the follow-up GET will
    // fire this event again and we'll catch it then.
    if (fetchOptions.method && fetchOptions.method.toUpperCase() !== "GET") return;

    if (consumeIntentionalLocationSwitch(url.toString())) return;

    const cross = isCrossLocationNavigation(url.toString());
    if (!cross) return;

    // Cancelled outright rather than resumed — a resumed Turbo fetch would be a soft visit.
    event.preventDefault();
    confirmThenNavigate(cross, url.toString());
  }) as EventListener);

  // Clean up stale dialogs when turbo caches the page (back/forward nav).
  document.addEventListener("turbo:before-cache", () => {
    cleanupDialog();
  });
}

/**
 * Installs a guard on Inertia router visits that shows a confirmation
 * dialog before switching location portals.
 */
export function installCrossLocationInertiaGuard() {
  router.on("before", (event) => {
    if (pending) return true;

    const visit = event.detail.visit;

    // skip hover prefetches — the user hasn't actually clicked anything
    if (visit.prefetch) return true;

    // skip non-GET visits — those are actions, not navigations. If the
    // response triggers a cross-location redirect, the follow-up GET will
    // fire this event again and we'll catch it then.
    if (visit.method.toUpperCase() !== "GET") return true;

    const cross = isCrossLocationNavigation(visit.url.toString());
    if (!cross) return true;

    confirmThenNavigate(cross, visit.url.toString());
    return false;
  });
}
